'use client';

import { forwardRef, useEffect, useState } from 'react';

import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import type { UnstyledButtonProps } from '../unstyled-button';
import { UnstyledButton } from '../unstyled-button';

const chipVariants = tv({
  slots: {
    base: 'inline-flex items-center justify-center rounded border transition-all cursor-default data-[checked=true]:bg-primary',
    icon: 'inline-flex items-center text-primary-500',
    childrenStyles:
      'data-[checked=true]:text-white data-[checked=false]:text-primary-500',
  },
  variants: {
    variant: {
      info: 'shadow-chip hover:bg-primary-200',
      default:
        'bg-primary-50 border-primary-100 shadow-chip hover:bg-primary-100',
      filter:
        'bg-white border-primary-300 hover:bg-primary-50 data-[checked=false]:text-primary data-[checked=true]:text-white',
      outline: {
        base: 'bg-transparent border-gray-700',
        childrenStyles: 'text-gray-700',
        icon: 'text-gray-600',
      },
    },
    size: {
      xs: 'text-body-4 leading-5 px-2',
      sm: 'text-body-4 leading-5 px-3 gap-1 pl-2 pr-1 py-0.5',
      md: 'text-body-3 leading-6 gap-2 px-3',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export type ChipProps = {
  id?: string;
  onChange?: (checked: boolean) => void;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  selected?: boolean;
  enableSelect?: boolean;
} & UnstyledButtonProps &
  VariantProps<typeof chipVariants>;

export const Chip = forwardRef<HTMLButtonElement, ChipProps>((props, ref) => {
  const {
    leftSection,
    children,
    rightSection,
    variant,
    size,
    className,
    onChange,
    selected,
    enableSelect = false,
    ...rest
  } = props;

  const { base, icon, childrenStyles } = chipVariants({
    variant,
    size,
    className,
  });

  const [isSelected, setIsSelected] = useState(selected);

  useEffect(() => {
    setIsSelected(selected);
  }, [selected]);

  return (
    <UnstyledButton
      data-checked={isSelected}
      className={base({
        className,
      })}
      onPress={() => {
        onChange?.(!isSelected);
        if (enableSelect) setIsSelected(!isSelected);
      }}
      ref={ref}
      {...rest}
    >
      <>
        {leftSection ? <span className={icon()}>{leftSection}</span> : null}
        {children ? (
          <span className={childrenStyles({ className })}>{children}</span>
        ) : null}
        {rightSection ? <span className={icon()}>{rightSection}</span> : null}
      </>
    </UnstyledButton>
  );
});

Chip.displayName = 'Chip';
